import {
  GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200,
  GetApiV1ThreeLetterCodePartnerAdminsCustomFieldDefinitions200,
  GetApiV1ThreeLetterCodePartnerAdminsGroups200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
} from '@fukuri-admins/apis/client/gen/model';
import { UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetPartnerAdminsPartnerFeatureFlags } from '~/hooks';
import { useGetClient } from '~/features/EditClientForm/hooks/useGetClient';
import { useGetCustomFieldDefinitions } from '~/features/EditClientForm/hooks/useGetCustomFieldDefinitions';
import { useGetGroups } from '~/features/EditClientForm/hooks/useGetGroups';
import { usePutPartnerAdminsClient } from '~/features/EditClientForm/hooks/usePutPartnerAdminsClient';
import { getClientLink } from '~/utils/getLinks';
import { convertToFormValues, generateRequestBody } from '../logics';
import { EditClientFormFieldValues, editClientSchema } from '../logics/schema';

export { useGetClient } from '~/features/EditClientForm/hooks/useGetClient';
export { useGetCustomFieldDefinitions } from '~/features/EditClientForm/hooks/useGetCustomFieldDefinitions';
export { useGetGroups } from '~/features/EditClientForm/hooks/useGetGroups';
export { usePutPartnerAdminsClient } from '~/features/EditClientForm/hooks/usePutPartnerAdminsClient';

type Args = {
  /** クライアントID */
  clientId: string;
};

type IUseLegacyEditClient = (args: Args) => {
  state: {
    methods: UseFormReturn<EditClientFormFieldValues>;
    isButtonDisabled: boolean;
    clientName: string;
    clientIdNum: number;
    customFieldDefinitions: GetApiV1ThreeLetterCodePartnerAdminsCustomFieldDefinitions200;
    groups: GetApiV1ThreeLetterCodePartnerAdminsGroups200;
    clientData: GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200;
    partnerFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200;
  };
  actions: {
    handleOnSubmit: () => void;
  };
};

/**
 * 利用企業編集フォームのContainer hook（Legacy）
 *
 * @returns state.methods - react-hook-formのメソッド
 * @returns state.isButtonDisabled - ボタン無効化フラグ
 * @returns state.clientName - 企業名
 * @returns state.clientIdNum - クライアントID（数値）
 * @returns state.customFieldDefinitions - カスタムフィールド定義
 * @returns state.groups - グループ一覧
 * @returns state.clientData - クライアントAPIレスポンス
 * @returns state.partnerFeatureFlags - パートナー機能フラグ一覧
 * @returns actions.handleOnSubmit - フォーム送信ハンドラ
 */
export const useLegacyEditClient: IUseLegacyEditClient = ({ clientId }) => {
  const navigate = useNavigate();
  const threeLetterCode = getThreeLetterCode();
  const { showToast } = useToast();

  const { data: clientData } = useGetClient({ threeLetterCode, clientId });
  const { data: customFieldDefinitionsData } = useGetCustomFieldDefinitions({ threeLetterCode });
  const { data: groupsData } = useGetGroups({ threeLetterCode });
  const { data: partnerFeatureFlagsData } = useGetPartnerAdminsPartnerFeatureFlags({
    threeLetterCode,
  });

  const clientName = clientData.client.name;
  const clientIdNum = clientData.client.id;

  const methods = useForm<EditClientFormFieldValues>({
    defaultValues: convertToFormValues({ clientData }),
    criteriaMode: 'all',
    mode: 'onTouched',
    resolver: zodResolver(editClientSchema),
  });

  const { trigger, isMutating } = usePutPartnerAdminsClient(threeLetterCode, clientId, {
    onSuccess: ({ client: { id, name } }) => {
      showToast({
        message: `「${name}」を更新しました。`,
        status: 'success',
      });
      navigate(getClientLink(id));
    },
    onError: error => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        showToast({
          message: errors?.[0] ?? '利用企業の更新に失敗しました。',
          status: 'danger',
        });
        return;
      }

      showToast({
        message: '利用企業の更新に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleOnSubmit = methods.handleSubmit(async formValues => {
    const requestBody = generateRequestBody({
      formValues,
      allFeatureFlags: clientData.client.client_feature_flags ?? [],
    });
    await trigger(requestBody);
  });

  const isButtonDisabled =
    methods.formState.isSubmitting || !methods.formState.isValid || isMutating;

  return {
    state: {
      methods,
      isButtonDisabled,
      clientName,
      clientIdNum,
      customFieldDefinitions: customFieldDefinitionsData,
      groups: groupsData,
      clientData,
      partnerFeatureFlags: partnerFeatureFlagsData,
    },
    actions: { handleOnSubmit },
  };
};
